import { Theme } from '@mui/material/styles';
import { Id, toast, ToastOptions } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export class ToastUtility {
  private static theme: Theme | null = null;

  // Initialize the utility with a MUI theme
  static init(muiTheme: Theme) {
    this.theme = muiTheme;
  }

  // Base method for creating toasts with theme-aware styling
  private static createToast(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info',
    customOptions: ToastOptions = {},
  ): Id {
    // Throw an error if theme hasn't been initialized
    if (!this.theme) {
      console.warn(
        'ToastUtility must be initialized with a MUI theme using ToastUtility.init()',
      );
      return toast(message, {
        type,
        ...customOptions,
      });
    }

    // Define theme-based styles based on toast type
    const themeStyles = {
      success: {
        background: `linear-gradient(to right, ${this.theme.palette.primary.main}, ${this.theme.palette.primary.light})`,
        color: this.theme.palette.common.white,
      },
      error: {
        background: `linear-gradient(to right, ${this.theme.palette.error.main}, ${this.theme.palette.error.light})`,
        color: this.theme.palette.common.white,
      },
      warning: {
        background: `linear-gradient(to right, ${this.theme.palette.warning.main}, ${this.theme.palette.warning.light})`,
        color: this.theme.palette.common.white,
      },
      info: {
        background: `linear-gradient(to right, ${this.theme.palette.info.main}, ${this.theme.palette.info.light})`,
        color: this.theme.palette.common.white,
      },
    };

    return toast(message, {
      type,
      style: {
        ...themeStyles[type],
        boxShadow: this.theme.shadows[3],
      },
      ...customOptions,
    });
  }

  // Public methods for different toast types
  static success(message: string, options: ToastOptions = {}): Id {
    return this.createToast(message, 'success', options);
  }

  static error(message: string, options: ToastOptions = {}): Id {
    return this.createToast(message, 'error', options);
  }

  static warning(message: string, options: ToastOptions = {}): Id {
    return this.createToast(message, 'warning', options);
  }

  static info(message: string, options: ToastOptions = {}): Id {
    return this.createToast(message, 'info', options);
  }

  // Expose react-toastify methods for additional control
  static dismiss(toastId?: Id) {
    toast.dismiss(toastId);
  }

  static clearAll() {
    toast.clearWaitingQueue();
  }
}
